import { useRef } from "react";
import { ClusterCard } from "./ClusterCard";
import { RunSnapshotCard } from "./RunSnapshotCard";
import type { SeverityThresholds, TapeRunGroup } from "./types";
import { Button } from "@/components/ui/button";
import { Loader2 } from "@/components/icons";
import { InfiniteScrollSentinel } from "@/components/InfiniteScrollSentinel";

interface TapeProps {
  groups: TapeRunGroup[];
  expandedSnapshotIds: Set<string>;
  expandedClusterIds: Set<string>;
  selectedItemId: string | null;
  highlightedItemId: string | null;
  thresholds: SeverityThresholds;
  now: number;
  materialId: string;
  otherMaterialId: string;
  notesCountsMap?: Record<string, number>;
  onToggleSnapshot: (snapshotId: string) => void;
  onToggleCluster: (clusterId: string) => void;
  onOpenArticle: (articleId: string, siblingIds: string[]) => void;
  onJumpToOtherPane: (clusterId: string, materialId: string) => void;
  onOpenClusterNotes?: (clusterId: string, title: string) => void;
  /** Called when the older-runs sentinel becomes visible. */
  onLoadOlderRuns: () => void;
  /** True while a fetchNextPage is in flight. Shows a spinner; disables the sentinel. */
  isLoadingOlder?: boolean;
  /** True when there are no more older runs to fetch. Hides the sentinel + fallback button. */
  hasMoreOlder?: boolean;
}

export function Tape({
  groups,
  expandedSnapshotIds,
  expandedClusterIds,
  selectedItemId,
  highlightedItemId,
  thresholds,
  now,
  materialId,
  otherMaterialId,
  notesCountsMap,
  onToggleSnapshot,
  onToggleCluster,
  onOpenArticle,
  onJumpToOtherPane,
  onOpenClusterNotes,
  onLoadOlderRuns,
  isLoadingOlder,
  hasMoreOlder = true,
}: TapeProps) {
  const tapeRef = useRef<HTMLElement | null>(null);
  const jumpToCluster = (clusterId: string) => {
    tapeRef.current
      ?.querySelector<HTMLElement>(`[data-cluster-id="${clusterId}"]`)
      ?.scrollIntoView({ block: "center" });
  };

  return (
    <section className="flex flex-col gap-4 p-3 overflow-y-auto" aria-label="Analysis history tape" ref={tapeRef}>
      {groups.map((group, index) => (
        <div className="flex flex-col gap-2" key={group.snapshot.id}>
          <RunSnapshotCard
            expanded={expandedSnapshotIds.has(group.snapshot.id)}
            group={group}
            highlighted={highlightedItemId === group.snapshot.id}
            onClusterJump={jumpToCluster}
            onToggle={onToggleSnapshot}
            previousSnapshot={groups[index + 1]?.snapshot ?? null}
            selected={selectedItemId === group.snapshot.id}
          />
          <div className="flex flex-col gap-2 ms-4">
            {group.clusters.map(({ cluster, consumed, driverWeight }) => (
              <ClusterCard
                cluster={cluster}
                expanded={expandedClusterIds.has(cluster.cluster_id)}
                highlighted={highlightedItemId === cluster.cluster_id}
                key={cluster.cluster_id}
                materialId={materialId}
                now={now}
                notesCount={notesCountsMap?.[`cluster:${cluster.cluster_id}`] ?? 0}
                onJumpToOtherPane={onJumpToOtherPane}
                onOpenArticle={onOpenArticle}
                onOpenNotes={onOpenClusterNotes ? () => onOpenClusterNotes(cluster.cluster_id, cluster.representative_title) : undefined}
                onToggle={onToggleCluster}
                otherMaterialId={otherMaterialId}
                selected={selectedItemId === cluster.cluster_id}
                thresholds={thresholds}
                variant={consumed ? "consumed" : "not-in-run"}
                driverWeight={driverWeight}
              />
            ))}
          </div>
        </div>
      ))}
      {isLoadingOlder ? (
        <div className="flex items-center gap-2 self-start px-2 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          Loading older runs…
        </div>
      ) : null}
      {hasMoreOlder ? (
        <>
          {/* Fallback manual trigger for keyboard / reduced-motion users; the
              sentinel auto-loads on scroll. */}
          <Button
            variant="link"
            onClick={onLoadOlderRuns}
            className="self-start"
            disabled={isLoadingOlder}
          >
            Load older runs
          </Button>
          <InfiniteScrollSentinel
            onVisible={onLoadOlderRuns}
            disabled={isLoadingOlder}
          />
        </>
      ) : null}
    </section>
  );
}
